import ApplicationController from './application_controller';

const ACTIVE_DROPZONE_CLASS = 'c-pqi-uploader__dropzone--active';
const ERROR_CLASS = 'c-pqi-uploader__file-item--error';
const LOADING_DROPZONE_CLASS = 'c-pqi-uploader__dropzone--loading';
const GENERIC_ERROR = 'The image could not be uploaded. Please try again.';

// Uploads photo evidence for a single Product Quality Issue item. Images are
// stored as soon as they are picked, and their ids are carried into the form
// submission through hidden inputs.
export default class ProductQualityIssueImageUploadController extends ApplicationController {
  static targets = ['fileInput', 'dropzone', 'fileList'];

  // The limits are mirrored from ProductQualityIssueItemImage
  static values = {
    oliId: Number,
    billEmail: String,
    minImages: Number,
    maxImages: Number,
    maxFileSize: Number,
    uploadUrl: String,
    deleteUrlTemplate: String,
    fieldPrefix: String,
    acceptedFormats: String,
  };

  initialize() {
    this.pendingUploads = 0;
  }


  openFilePicker() {
    if (this.isEnabled) this.fileInputTarget.click();
  }

  dragOver(event) {
    event.preventDefault();
    if (this.isEnabled) this.dropzoneTarget.classList.add(ACTIVE_DROPZONE_CLASS);
  }

  dragLeave() {
    this.dropzoneTarget.classList.remove(ACTIVE_DROPZONE_CLASS);
  }

  drop(event) {
    event.preventDefault();
    this.dropzoneTarget.classList.remove(ACTIVE_DROPZONE_CLASS);
    this.upload(Array.from(event.dataTransfer.files));
  }

  fileInputChanged() {
    this.upload(Array.from(this.fileInputTarget.files));
    this.fileInputTarget.value = '';
  }

  removeImage(event) {
    const { imageId } = event.params;
    const url = `${this.deleteUrlTemplateValue.replace('__ID__', imageId)}?${this.authQuery}`;

    fetch(url, {
      method: 'DELETE',
      headers: { 'X-CSRF-Token': this.csrfToken },
    })
      .then(response => response.json())
      .then((data) => {
        if (!data.success) throw new Error(data.error || 'Delete failed');

        [this.fileItemFor(imageId), this.hiddenInputFor(imageId)]
          .filter(Boolean)
          .forEach(element => element.remove());
      })
      .catch((error) => {
        this.showError('The image could not be removed. Please try again.');
        this.handleError(error);
      });
  }

  // Fired by the product quality issue form on submit. Cancelling the event
  // tells the form this item is not ready to be submitted.
  validate(event) {
    this.clearErrors();

    if (this.pendingUploads > 0) {
      this.showError('Please wait for the images to finish uploading.');
      event.preventDefault();
    } else if (this.imageIds.length < this.minImagesValue) {
      this.showError(`Please upload at least ${this.minImagesValue} images.`);
      event.preventDefault();
    }
  }

  upload(files) {
    if (!this.isEnabled || files.length === 0) return;

    this.clearErrors();

    const remaining = this.maxImagesValue - this.imageCount;

    if (remaining <= 0) {
      this.showError(`A maximum of ${this.maxImagesValue} images can be uploaded.`);
      return;
    }

    if (files.length > remaining) {
      this.showError(`Only ${remaining} more image(s) can be uploaded.`);
    }

    files.slice(0, remaining).forEach(file => this.uploadFile(file));
  }

  uploadFile(file) {
    const invalidReason = this.fileError(file);

    if (invalidReason) {
      this.showError(`${file.name}: ${invalidReason}`);
      return;
    }

    this.startUpload();

    fetch(this.uploadUrlValue, {
      method: 'POST',
      headers: { 'X-CSRF-Token': this.csrfToken },
      body: this.uploadBody(file),
    })
      .then(response => response.json().catch(() => ({ error: GENERIC_ERROR })))
      .then((data) => {
        if (data.error) throw new Error(data.error);

        this.addImage(data.id, file.name);
      })
      .catch((error) => {
        this.showError(`${file.name}: ${error.message || GENERIC_ERROR}`);
        this.handleError(error);
      })
      .finally(() => this.finishUpload());
  }

  uploadBody(file) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('order_line_item_id', this.oliIdValue);
    formData.append('bill_email', this.billEmailValue);

    return formData;
  }

  fileError(file) {
    if (!this.allowedTypes.includes(file.type)) {
      return `file type not allowed. Accepted formats: ${this.acceptedFormatsValue}.`;
    }

    if (file.size > this.maxFileSizeValue) {
      return `file size exceeds ${this.maxFileSizeValue / (1024 * 1024)}MB. Please upload a smaller image.`;
    }

    return null;
  }

  addImage(imageId, fileName) {
    this.fileListTarget.appendChild(this.buildFileItem(imageId, fileName));
    this.element.appendChild(this.buildHiddenInput(imageId));
  }

  buildFileItem(imageId, fileName) {
    const item = document.createElement('div');
    item.classList.add('c-pqi-uploader__file-item');
    item.dataset.imageId = imageId;

    const name = document.createElement('span');
    name.textContent = fileName;

    const removeButton = document.createElement('button');
    removeButton.type = 'button';
    removeButton.classList.add('c-pqi-uploader__file-remove');
    removeButton.setAttribute('aria-label', `Remove ${fileName}`);
    removeButton.textContent = '×';
    removeButton.dataset.action = 'click->product-quality-issue-image-upload#removeImage';
    removeButton.dataset.productQualityIssueImageUploadImageIdParam = imageId;

    item.appendChild(name);
    item.appendChild(removeButton);

    return item;
  }

  buildHiddenInput(imageId) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = `${this.fieldPrefixValue}[image_ids][]`;
    input.value = imageId;
    input.dataset.imageId = imageId;

    return input;
  }

  showError(message) {
    const error = document.createElement('div');
    error.classList.add('c-pqi-uploader__file-item', ERROR_CLASS);
    error.textContent = message;
    this.fileListTarget.appendChild(error);
  }

  clearErrors() {
    this.fileListTarget.querySelectorAll(`.${ERROR_CLASS}`).forEach(error => error.remove());
  }

  startUpload() {
    this.pendingUploads += 1;
    this.dropzoneTarget.classList.add(LOADING_DROPZONE_CLASS);
  }

  finishUpload() {
    this.pendingUploads = Math.max(this.pendingUploads - 1, 0);
    if (this.pendingUploads === 0) this.dropzoneTarget.classList.remove(LOADING_DROPZONE_CLASS);
  }

  fileItemFor(imageId) {
    return this.fileListTarget.querySelector(`[data-image-id="${imageId}"]`);
  }

  hiddenInputFor(imageId) {
    return this.element.querySelector(`input[type="hidden"][data-image-id="${imageId}"]`);
  }

  get isEnabled() {
    return !this.fileInputTarget.disabled;
  }

  get allowedTypes() {
    return this.fileInputTarget.accept.split(',');
  }

  get imageIds() {
    return Array.from(this.element.querySelectorAll('input[type="hidden"][data-image-id]'))
      .map(input => input.value);
  }

  get imageCount() {
    return this.imageIds.length + this.pendingUploads;
  }

  get authQuery() {
    return new URLSearchParams({ bill_email: this.billEmailValue }).toString();
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content;
  }
}
